import { InfoOutlined } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import type { DateTime } from "luxon";
import { memo, useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { SURFACE } from "../../theme/tokens";
import { ALF_DEMO_AIDE_RATIO, ALF_DEMO_NURSE_RATIO } from "../../utils/consts";
import { hospitalRatioBadgeForDate } from "../../utils/hospitalDemoScore";
import type { DayStats } from "../../utils/models";
import { formatNumber, getChipColorForPercentage } from "../../utils/utilFunctions";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

type Props = { dayStats: DayStats; colors: { backgroundColor: string; color: string }; isActual?: boolean; date?: DateTime };

const NurseHoursChip = memo<Props>(({ colors, dayStats, isActual, date }) => {
	const { isAlf, isHospitalDemo, isHospitalView, hospitalData, chosenDepartment } = useHospitalData();
	const isHospitalRatio = isHospitalView || isHospitalDemo;
	// Hospital badge: staff on the day (actual on past days with punches — the parent picks the stats)
	// against the sum of every shift's node (staffNo) on that date. Not an hours %.
	const hospitalBadge = useMemo(
		() => (isHospitalRatio ? hospitalRatioBadgeForDate(dayStats.totalStaff, hospitalData.weeks, date) : null),
		[isHospitalRatio, dayStats.totalStaff, date, hospitalData.weeks],
	);
	// The hospital badge is coloured by its own % (0% when nothing is required), with the same
	// thresholds the hours chip uses.
	const chipColors = hospitalBadge ? getChipColorForPercentage(hospitalBadge.percentage, chosenDepartment) : colors;
	let rawPercentage = 0;
	let ratio = 0; // For ALF, we use this later to show in the ui
	if (isAlf) {
		ratio = dayStats.totalStaff > 0 ? Math.round(dayStats.census / ALF_DEMO_AIDE_RATIO) + Math.round(dayStats.census / ALF_DEMO_NURSE_RATIO) : 0;
		rawPercentage = ratio > 0 ? Math.round((dayStats.totalStaff / ratio) * 100) : 0;
	} else if (hospitalBadge) {
		rawPercentage = hospitalBadge.percentage;
	} else {
		rawPercentage = dayStats.wantedHours > 0 ? Math.round((dayStats.totalHours / dayStats.wantedHours) * 100) : 0;
	}

	const percentage = Number.isFinite(rawPercentage) ? rawPercentage : 0;

	// The badge counts NURSES against the ratio ladder, but this tooltip counts HOURS against the
	// HPPD target. A ratio department has no HPPD target, so the two never agree and the tooltip
	// only confuses: 100% on the badge beside "138 / 98 hours". Hidden on real hospital demos.
	const chip = (
		<Chip
			sx={{
				backgroundColor: chipColors.backgroundColor,
				color: chipColors.color,
				p: 0,
				height: "20px",
				paddingLeft: "3px",
				paddingRight: "3px",
				"& .MuiChip-label": {
					px: 0.5,
					fontSize: "0.7rem",
					lineHeight: 10,
					fontWeight: 500,
				},
			}}
			label={
				<span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
					{`${formatNumber(percentage)}%`}
					<span
						style={{
							fontSize: "0.8125rem",
							fontWeight: 500,
							color: chipColors.color,
							marginLeft: "4px",
							display: "flex",
							alignItems: "center",
						}}
					>
						<img
							height="14px"
							src="/nurse.png"
							alt="nurse"
							style={{
								margin: "0",
								filter: chipColors.color.toLowerCase() === SURFACE.toLowerCase() ? "brightness(0) invert(1)" : "none",
							}}
						/>
						<span>
							{isAlf
								? `${dayStats.totalStaff} / ${ratio}`
								: hospitalBadge
									? `${hospitalBadge.numerator} / ${hospitalBadge.denominator}`
									: `${formatNumber(dayStats.totalHours / dayStats.census)} / ${formatNumber(dayStats.wantedHours / dayStats.census)}`}
						</span>
					</span>
				</span>
			}
		/>
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center" }}>
			{isHospitalView ? (
				chip
			) : (
				<InHouseTooltip title={`Staffing Percentage: ${Math.ceil(dayStats.totalHours)} / ${Math.ceil(dayStats.wantedHours)} hours`}>{chip}</InHouseTooltip>
			)}
			{isActual && (
				<InHouseTooltip title="Actual Stats">
					<InfoOutlined sx={{ fontSize: "18px" }} />
				</InHouseTooltip>
			)}
		</Box>
	);
});

export default NurseHoursChip;
